import {
  Box,
  Stack,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { styled } from '@mui/material/styles';
import { useUserSavings } from '@/hooks/useUsers';
import { formatCurrencyAmount } from '@liminal/utils/currency';
import { truncateMiddle } from '@liminal/utils/truncate';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { StatGrid } from '@liminal/web-ui/display/StatGrid';
import { EmptyState } from '@liminal/web-ui/feedback/EmptyState';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { MonoCell } from '@/components/ui/MonoCell';

// -- Styled components --

const CardHeader = styled(Box)({
  paddingLeft: 16,
  paddingRight: 16,
  paddingTop: 16,
  paddingBottom: 8,
});

const PositionsHeading = styled(Typography)({
  paddingLeft: 16,
  paddingRight: 16,
  paddingBottom: 4,
});

export function SavingsSection({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useUserSavings(userId);

  // data is GetBalanceResponse with balances (AssetBalance[]) and total (TotalBalance)
  const balances = data?.balances ?? [];
  const total = data?.total;

  return (
    <Stack spacing={3}>
      {/* A failed request says so. It used to read "Savings data will appear
          here when available", which is what an account with no savings says. */}
      {isError && <WarningCard tone="error">Could not load this user's savings.</WarningCard>}

      {/* Total Summary */}
      {(isLoading || total) && (
        <Stack spacing={1}>
          <Typography variant="h6">Savings Summary</Typography>
          <StatGrid
            columns={3}
            stats={[
              {
                id: 'total-value',
                label: 'Total Value (USD)',
                value: formatCurrencyAmount(total?.totalValueUsd, 'USD'),
                loading: isLoading,
              },
              {
                id: 'total-interest',
                label: 'Total Interest (USD)',
                value: formatCurrencyAmount(total?.totalInterestUsd, 'USD'),
                loading: isLoading,
              },
              {
                id: 'average-apy',
                label: 'Average APY',
                value: `${total?.averageApy ?? '0'}%`,
                tone: 'neutral',
                loading: isLoading,
              },
            ]}
          />
        </Stack>
      )}

      {isLoading && (
        <Card variant="elevated">
            <Skeleton height={200} />
        </Card>
      )}

      {/* Per-asset savings cards */}
      {balances.map((asset, ai) => (
        <Card key={asset.assetSymbol ?? ai} variant="elevated" padding="none">
            <CardHeader>
              <Typography variant="h6">{asset.assetSymbol ?? 'Unknown'}</Typography>
              <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <DetailRow layout="stacked" label="Total Balance" value={asset.totalBalance ?? '0'} bold />
                <DetailRow layout="stacked" label="Total Deposited" value={asset.totalDeposited ?? '0'} />
                <DetailRow layout="stacked" label="Total Withdrawn" value={asset.totalWithdrawn ?? '0'} />
                <DetailRow
                  layout="stacked"
                  label="Daily Earning Rate"
                  value={asset.dailyEarningRate ?? '0'}
                />
                {asset.interest && (
                  <>
                    <DetailRow
                      layout="stacked"
                      label="Current Interest"
                      value={asset.interest.current ?? '0'}
                    />
                    <DetailRow
                      layout="stacked"
                      label="Lifetime Interest"
                      value={asset.interest.lifetime ?? '0'}
                    />
                  </>
                )}
              </Stack>
            </CardHeader>

            {/* Positions table */}
            {asset.positions && asset.positions.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <PositionsHeading variant="subtitle2">Positions</PositionsHeading>
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 1100 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Chain</TableCell>
                        <TableCell>Protocol</TableCell>
                        <TableCell>Vault Name</TableCell>
                        <TableCell>Vault Address</TableCell>
                        <TableCell>Balance</TableCell>
                        <TableCell>Share Balance</TableCell>
                        <TableCell>Base APY</TableCell>
                        <TableCell>Rewards APR</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {asset.positions.map((pos, pi) => (
                        <TableRow key={pi}>
                          <TableCell>
                            <StatusPill label={pos.chain ?? '—'} tone="info" size="sm" />
                          </TableCell>
                          <TableCell>{pos.protocol ?? '—'}</TableCell>
                          <TableCell>{pos.vaultName ?? '—'}</TableCell>
                          <MonoCell>
                            {pos.vaultAddress ? truncateMiddle(pos.vaultAddress) : '—'}
                          </MonoCell>
                          <TableCell>{pos.balance ?? '0'}</TableCell>
                          <TableCell>{pos.shareBalance ?? '0'}</TableCell>
                          <TableCell>{pos.baseApy ?? '0'}%</TableCell>
                          <TableCell>{pos.rewardsApr ?? '0'}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
        </Card>
      ))}

      {!isLoading && !isError && balances.length === 0 && (
        <EmptyState icon="cash" size="sm" title="No savings positions" />
      )}
    </Stack>
  );
}
